import React, { useState } from 'react';
import { DoubleRightOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Spin } from 'antd';
import styles from './index.module.scss';
import iconToday from '../../imgs/icon-today.png';
import iconYear from '../../imgs/icon-year.png';
import StationDetailModal from '../StationDetailModal';
import {
  apiV2AirCompressorOverviewTotalPost,
  V2AirCompressorOverviewTotalPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { formatNumber, formatValueWithWan, OVERVIEW_POLLING_INTERVAL } from '../../utils';

type AggregateMetric = {
  powerLabel: string;
  powerValue: string;
  unit: string;
  topLabel: string;
  topValue: string;
  topValueSuffix: string;
  topUnit: string;
  bottomLabel: string;
  bottomValue: string;
  bottomValueSuffix: string;
  bottomUnit: string;
};

const renderMetricSection = (metric: AggregateMetric, iconSrc: string, iconPanelClassName: string) => (
  <div className={styles.metricSection}>
    <div className={iconPanelClassName}>
      <img src={iconSrc} alt="" className={styles.iconImage} />
      <div className={styles.iconLabel}>{metric.powerLabel}</div>
      <div className={styles.iconValue}>{metric.powerValue}</div>
      <div className={styles.iconUnit}>{metric.unit}</div>
    </div>

    <div className={styles.textPanel}>
      <div className={styles.textGroup}>
        <span className={styles.metricLabel}>{`${metric.topLabel}(${metric.topUnit})`}</span>
        <div className={styles.metricValueLine}>
          <strong className={styles.primaryValue}>{metric.topValue}</strong>
          {metric.topValueSuffix && <span className={styles.primaryValueSuffix}>{metric.topValueSuffix}</span>}
        </div>
      </div>
      <div className={styles.textGroup}>
        <span className={styles.metricLabel}>{`${metric.bottomLabel}(${metric.bottomUnit})`}</span>
        <div className={styles.metricValueLine}>
          <strong className={styles.secondaryValue}>{metric.bottomValue}</strong>
          {metric.bottomValueSuffix && <span className={styles.secondaryValueSuffix}>{metric.bottomValueSuffix}</span>}
        </div>
      </div>
    </div>
  </div>
);

const mapResponseToAggregateData = (data?: V2AirCompressorOverviewTotalPostResponse) => {
  const todayGas = formatValueWithWan(data?.gasProduction, data?.gasUnit);
  const todayPower = formatValueWithWan(data?.powerConsumption, data?.electricUnit);
  const yearGas = formatValueWithWan(data?.yearGasProduction, data?.gasUnit);
  const yearPower = formatValueWithWan(data?.yearPowerConsumption, data?.electricUnit);

  return {
    today: {
      powerLabel: '今日整体气电比',
      powerValue: formatNumber(data?.gasPowerRatio, 4),
      unit: data?.gasElectricUnit || '-',
      topLabel: '今日产气量',
      topValue: todayGas.displayValue,
      topValueSuffix: todayGas.valueSuffix,
      topUnit: todayGas.displayUnit,
      bottomLabel: '今日耗电量',
      bottomValue: todayPower.displayValue,
      bottomValueSuffix: todayPower.valueSuffix,
      bottomUnit: todayPower.displayUnit,
    },
    year: {
      powerLabel: '本年整体气电比',
      powerValue: formatNumber(data?.yearGasPowerRatio, 4),
      unit: data?.gasElectricUnit || '-',
      topLabel: '本年产气量',
      topValue: yearGas.displayValue,
      topValueSuffix: yearGas.valueSuffix,
      topUnit: yearGas.displayUnit,
      bottomLabel: '本年耗电量',
      bottomValue: yearPower.displayValue,
      bottomValueSuffix: yearPower.valueSuffix,
      bottomUnit: yearPower.displayUnit,
    },
    detailText: '详情',
  };
};

const OverviewAggregate: React.FC = () => {
  const { data, loading } = useRequest(() => apiV2AirCompressorOverviewTotalPost({}), {
    pollingInterval: OVERVIEW_POLLING_INTERVAL,
  });
  const aggregateData = mapResponseToAggregateData(data);
  const [detailOpen, setDetailOpen] = useState(false);

  return (
    <>
      <Spin spinning={loading && !data} wrapperClassName={styles.aggregateLoading}>
        <section className={styles.aggregateCard}>
          {renderMetricSection(aggregateData.today, iconToday, styles.iconPanelToday)}
          {renderMetricSection(aggregateData.year, iconYear, styles.iconPanelYear)}

          <div className={styles.detailColumn}>
            <div className={styles.detailLink} onClick={() => setDetailOpen(true)}>
              <span>{aggregateData.detailText}</span>
              <DoubleRightOutlined />
            </div>
          </div>
        </section>
      </Spin>

      <StationDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        stationTitle="空压站总览"
        scopeType="overall"
        stationIds={(data?.compressDataVoList ?? [])
          .map(item => item.id)
          .filter((id): id is number => typeof id === 'number')}
      />
    </>
  );
};

export default OverviewAggregate;
